import { Observable, firstValueFrom } from "rxjs";
import {
  AuthService,
  buildParamString,
  IAPICommonResponse,
  IAPIResponse,
  // HttpService
} from "@mindmemobile/mm-shared-global";
import HttpService from "./HttpService";
import { url } from "../components/FundraiserAPIEndPoints";
import {
  IAPIFundraiserCommonResponse,
  IAPIFundRaiserDashboardResult,
  IAPIFundraiserDonationInsightsModel,
  IAPIFundraiserGridModel,
  IPromoter,
  ICreateFundraiserIAPIResponse,
  IUpdateFundraiserIAPIResponse,
  ISubscription,
  IAPIGetFundraiserPublicViewResponseModel,
  IAPIDonationItemIAPIResponse,
  IAPIDonationItem,
  IAPIGetListInterestTagResponseModel,
  IUpdateStatus,
  IAPIItemsGridModel,
  IContactReferenceDataModel,
  IAPIFundraiserDonationModel,
  IFundraisersDonationsBoardResponse,
  IAPIJoinFundRaiserRequest,
  IUpdateDonationRequestModel,
  IAPIEditPromoterRequest,
  IMindMeMobilePage,
  IAPIDonationItems,
  IAPICreatePromoterRequest,
  IAPICreateMultiplePromoterRequest,
  IFundraisersDonationsSubscriptionResponse,
  IAPIAddPromoterRequest,
  ISaveOpenAISettingsRequestModel,
  IUpdateOpenAISettingsRequestModel 
} from "../interfaces";
import { FundRaiserStatus } from "../enums";
import {
  IGallerySettings,
  IUpdateFundraiserURLRequestModel,
  IUpdateGalleryURLRequestModel,
  IUpdateMediaTitleAndDescriptionRequestModel,
} from "../interfaces/IGalleryModels";
import {
  IAPIApplicationSubAccountBusinessModel,
  IAPICancelSubscriptionModel,
  IAPICategoryModel,
  IAPIMerchantAccountModel,
  IAPIPaymentRefundModel,
} from "@mindmemobile/mm-payment-modules";


class FundraiserService {
  private httpService: HttpService;
  constructor() {
    this.httpService = new HttpService(
      undefined,
      `${process.env.REACT_APP_API_KEY}`,
      process.env.REACT_APP_API_BASE_URL
    );
  }

  createFundraiser(
    request: ICreateFundraiserIAPIResponse
  ): Observable<IAPIResponse<string>> {
    return this.httpService.post<IAPIResponse<string>>(
      url.createFundraiser,
      request
    );
  }

  updateFundraiser(
    request: IUpdateFundraiserIAPIResponse
  ): Observable<IAPIResponse<boolean>> {
    return this.httpService.post<IAPIResponse<boolean>>(
      url.updateFundraiser,
      request
    );
  }

  getFundraisers(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    minAmountRange?: number,
    maxAmountRange?: number,
    startDate?: Date,
    endDate?: Date,
    galleryId?: string,
    status?: FundRaiserStatus[],
    promoterId?: string
  ): Observable<IAPIResponse<IAPIFundraiserGridModel[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
      minAmountRange: minAmountRange,
      maxAmountRange: maxAmountRange,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      galleryId: galleryId,
      status: status?.join(","),
      promoterId: promoterId,
    });
    return this.httpService.get<IAPIResponse<IAPIFundraiserGridModel[]>>(
      url.getFundraisers,
      params
    );
  }

  deleteFundraiser(accountId: string, id: string, userId: string) {
    const request = {
      id: id,
      accountId: accountId,
      userId: userId,
    };
    return this.httpService.delete<IAPIResponse<IAPICommonResponse>>(
      url.deleteFundraiser,
      request
    );
  }

  copyFundraiser(
    accountId: string,
    id: string,
    userId: string,
    name: string
  ): Observable<IAPIResponse<IAPIFundraiserCommonResponse>> {
    const request = {
      id: id,
      accountId: accountId,
      userId: userId,
      name: name,
    };
    return this.httpService.post<IAPIResponse<IAPIFundraiserCommonResponse>>(
      url.copyFundraiser,
      request
    );
  }

  getDonationInsights(
    accountId: string,
    fundRaiserId: string
  ): Observable<IAPIResponse<IAPIFundraiserDonationInsightsModel>> {
    const params: string = buildParamString({
      accountId: accountId,
      fundRaiserId: fundRaiserId,
    });
    return this.httpService.get<
      IAPIResponse<IAPIFundraiserDonationInsightsModel>
    >(url.getDonationInsights, params);
  }

  getDashboradData(
    accountId: string
  ): Observable<IAPIResponse<IAPIFundRaiserDashboardResult>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IAPIFundRaiserDashboardResult>>(
      url.getDashboardData,
      params
    );
  }

  getFundraiserByUrl(
    shortUniqueId: string,
    pageName: string,
    promoterId: string
  ): Observable<IAPIResponse<IAPIGetFundraiserPublicViewResponseModel>> {
    const params: string = buildParamString({
      shortUniqueId: shortUniqueId,
      pageName: pageName,
      promoterId: promoterId,
    });
    return this.httpService.get<
      IAPIResponse<IAPIGetFundraiserPublicViewResponseModel>
    >(url.getFundraiserByUrl, params);
  }

  updateDonationDetails(
    request: IUpdateDonationRequestModel
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.updateDonationDetails,
      request
    );
  }

  getFundraiserData(
    id: string | null,
    promoterId: string
  ): Observable<IAPIResponse<IAPIGetFundraiserPublicViewResponseModel>> {
    const params: string = buildParamString({
      id: id,
      promoterId: promoterId,
    });
    return this.httpService.get<
      IAPIResponse<IAPIGetFundraiserPublicViewResponseModel>
    >(url.getFundraiserData, params);
  }

  getPromotersDashboardData(
    promoterId: string,
    fundraiserId?: string
  ): Observable<IAPIResponse<IAPIGetFundraiserPublicViewResponseModel>> {
    const params: string = buildParamString({
      fundraiserId: fundraiserId,
      promoterId: promoterId,
    });
    return this.httpService.get<
      IAPIResponse<IAPIGetFundraiserPublicViewResponseModel>
    >(url.getPromoterDashboardData, params);
  }

  getPromoters(
    accountId: string,
    fundRaiserId: string,
    searchValue: string,
    pageNumber: number,
    pageSize: number
  ): Observable<IAPIResponse<IPromoter[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      fundRaiserId: fundRaiserId,
      searchValue: searchValue,
      pageNumber: pageNumber,
      pageSize: pageSize,
    });
    return this.httpService.get<IAPIResponse<IPromoter[]>>(
      url.getPromoters,
      params
    );
  }

  refundPayment(
    data: IAPIPaymentRefundModel
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.post<IAPIResponse<IAPICommonResponse>>(
      url.refundPayment,
      data
    );
  }

  getDonations(
    accountId: string,
    fundRaiserId: string,
    searchValue: string,
    pageNumber: number,
    pageSize: number
  ): Observable<IAPIResponse<IFundraisersDonationsBoardResponse[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      fundRaiserId: fundRaiserId,
      searchValue: searchValue,
      pageNumber: pageNumber,
      pageSize: pageSize,
    });
    return this.httpService.get<
      IAPIResponse<IFundraisersDonationsBoardResponse[]>
    >(url.getDonations, params);
  }

  getSubscriptions(
    accountId: string,
    fundRaiserId: string,
    searchValue: string,
    pageNumber: number,
    pageSize: number
  ): Observable<IAPIResponse<IFundraisersDonationsSubscriptionResponse[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      fundRaiserId: fundRaiserId,
      searchValue: searchValue,
      pageNumber: pageNumber,
      pageSize: pageSize,
    });
    return this.httpService.get<
      IAPIResponse<IFundraisersDonationsSubscriptionResponse[]>
    >(url.getSubscriptions, params);
  }

  /* Whole-tab downloads built by the API; the caller only saves the Blob. recurring=true exports the
     Recurring grid of the Donations tab, false the One Time grid. searchValue applies the same
     filter the tab uses. */
  exportDonations(
    accountId: string,
    fundRaiserId: string,
    recurring: boolean,
    format: "csv" | "xlsx",
    searchValue: string
  ): Observable<Blob> {
    const params: string = buildParamString({
      /* Sent as a string on purpose: buildParamString turns any falsy value into "", so a raw
         `false` would go out as `recurring=` and the API would fall back to its default. */
      accountId: accountId,
      fundRaiserId: fundRaiserId,
      recurring: recurring ? "true" : "false",
      format: format,
      searchValue: searchValue,
    });
    return this.httpService.getBlob(url.exportDonations, params);
  }

  exportDonors(
    accountId: string,
    fundRaiserId: string,
    format: "csv" | "xlsx",
    searchValue: string
  ): Observable<Blob> {
    const params: string = buildParamString({
      accountId: accountId,
      fundRaiserId: fundRaiserId,
      format: format,
      searchValue: searchValue,
    });
    return this.httpService.getBlob(url.exportDonors, params);
  }

  cancelSubscription(
    data: IAPICancelSubscriptionModel
  ): Observable<IAPIResponse<boolean>> {
    return this.httpService.put<IAPIResponse<boolean>>(
      url.cancelSubscription,
      data
    );
  }

  getGallerySettings(id: string): Observable<IAPIResponse<IGallerySettings>> {
    const params: string = buildParamString({
      id: id,
    });
    return this.httpService.get<IAPIResponse<IGallerySettings>>(
      url.getGallerySettings,
      params
    );
  }

  getGalleries(
    accountId: string
  ): Observable<IAPIResponse<IGallerySettings[]>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IGallerySettings[]>>(
      url.getGalleries,
      params
    );
  }

  updateGallerySettings(
    request: IGallerySettings
  ): Observable<IAPIResponse<boolean>> {
    return this.httpService.put<IAPIResponse<boolean>>(
      url.updateGallerySettings,
      request
    );
  }

  updateGalleryURL(
    data: IUpdateGalleryURLRequestModel
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.updateGalleryURL,
      data
    );
  }

  updateFundraiserPageName(
    data: IUpdateFundraiserURLRequestModel
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.updateFundraiserPageName,
      data
    );
  }

  getOpenAISettings(
    accountId: string
  ): Observable<IAPIResponse<IUpdateOpenAISettingsRequestModel>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<
      IAPIResponse<IUpdateOpenAISettingsRequestModel>
    >(url.getOpenAISettings, params);
  }

  saveOpenAISettings(
    data: ISaveOpenAISettingsRequestModel
  ): Observable<IAPIResponse<string>> {
    return this.httpService.post<IAPIResponse<string>>(
      url.saveOpenAISettings,
      data
    );
  }

  updateOpenAISettings(
    data: IUpdateOpenAISettingsRequestModel
  ): Observable<IAPIResponse<boolean>> {
    return this.httpService.post<IAPIResponse<boolean>>(
      url.updateOpenAISettings,
      data
    );
  }

  updateMediaTitleAndDescription(
    data: IUpdateMediaTitleAndDescriptionRequestModel
  ): Observable<IAPIResponse<boolean>> {
    return this.httpService.post<IAPIResponse<boolean>>(
      url.updateMediaTitleAndDescription,
      data
    );
  }

  saveGallerySettings(
    request: IGallerySettings
  ): Observable<IAPIResponse<string | null>> {
    return this.httpService.post<IAPIResponse<string | null>>(
      url.saveGallerySettings,
      request
    );
  }

  getPublicGallery(
    shortUniqueId: string,
    galleryUrl: string
  ): Observable<IAPIResponse<IGallerySettings>> {
    const params: string = buildParamString({
      url: galleryUrl,
      shortUniqueId: shortUniqueId,
    });
    return this.httpService.get<IAPIResponse<IGallerySettings>>(
      url.getPublicGallery,
      params
    );
  }

  getDonationItems(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string
  ): Observable<IAPIResponse<IAPIDonationItem[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
    });
    return this.httpService.get<IAPIResponse<IAPIDonationItem[]>>(
      url.getDonationItems,
      params
    );
  }

  getMerchantAccounts(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string
  ): Observable<IAPIResponse<IAPIMerchantAccountModel[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
    });

    return this.httpService.get<IAPIResponse<IAPIMerchantAccountModel[]>>(
      url.getMerchantAccounts,
      params
    );
  }

  getMindMeListInterestTag(
    accountId: string
  ): Observable<IAPIResponse<IAPIGetListInterestTagResponseModel>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<
      IAPIResponse<IAPIGetListInterestTagResponseModel>
    >(url.getMindMeListInterestTag, params);
  }

  getMindMeContactReferences(
    accountId: string
  ): Observable<IAPIResponse<IContactReferenceDataModel>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IContactReferenceDataModel>>(
      url.getMindMeContactReferences,
      params
    );
  }

  updateStatus(
    id: string,
    accountId: string,
    userId: string,
    status: keyof typeof FundRaiserStatus
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    const data: IUpdateStatus = { id, accountId, userId, status };
    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.updateFundraiserStatus,
      data
    );
  }

  getCategories(
    accountId: string
  ): Observable<IAPIResponse<IAPICategoryModel[]>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IAPICategoryModel[]>>(
      url.getCategories,
      params
    );
  }

  getMindMeMobilePages(
    accountId: string
  ): Observable<IAPIResponse<IMindMeMobilePage[]>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IMindMeMobilePage[]>>(
      url.getMindMeMobilePages,
      params
    );
  }

  addNewPromoterToFundRaiser(
    request: IAPIJoinFundRaiserRequest
  ): Observable<IAPIResponse<IAPIAddPromoterRequest>> {
    return this.httpService.post<IAPIResponse<IAPIAddPromoterRequest>>(
      url.addNewPromoterToFundRaiser,
      request
    );
  }

  updatePromoter(
    request: IAPIEditPromoterRequest
  ): Observable<IAPIResponse<string>> {
    return this.httpService.post<IAPIResponse<string>>(
      url.updatePromoter,
      request
    );
  }

  getAllDonationItems(
    accountId: string
  ): Observable<IAPIResponse<IAPIDonationItems[]>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IAPIDonationItems[]>>(
      url.getAllDonationItems,
      params
    );
  }

  addNewPromoters(
    request: IAPICreateMultiplePromoterRequest
  ): Observable<IAPIResponse<string>> {
    return this.httpService.post<IAPIResponse<string>>(
      url.addNewPromoters,
      request
    );
  }

  updatePromoters(
    fundraiserId: string,
    contactIds: string[]
  ): Observable<IAPIResponse<boolean>> {
    const request = {
      accountId: AuthService.authData.accountId,
      fundRaiserId: fundraiserId,
      contactIds: contactIds,
    };
    return this.httpService.post<IAPIResponse<boolean>>(
      url.updatePromoters,
      request
    );
  }

  getAccountBusinessInfo(
    accountId: string
  ): Observable<IAPIResponse<IAPIApplicationSubAccountBusinessModel>> {
    const params: string = buildParamString({
      accountId: accountId,
    });
    return this.httpService.get<IAPIResponse<IAPIApplicationSubAccountBusinessModel>>(
      url.getAccountBusinessInfo,
      params
    );
  }


}
export default new FundraiserService();
